/**
 * สูตรคำนวณทั้งหมดของ Wickhaven ตาม docs/GDD.md
 *
 * ทุกฟังก์ชันในไฟล์นี้เป็น pure function รับค่าเข้าแล้วคืนค่าออก
 * ไม่อ่าน state ของผู้เล่นและไม่สุ่ม เพื่อให้ server ตัดสินผลได้
 * และ client เอาไปแสดงตัวเลขคาดการณ์ได้โดยได้ค่าตรงกัน
 *
 * ส่วนที่ต้องสุ่ม (โอกาสโดน คริ ดรอป) แยกเป็นฟังก์ชันที่คืน "โอกาส"
 * แล้วให้ฝั่ง server ทอยเต๋าเอง ตามกฎ server-authoritative
 */
import { STAT_KEYS } from "./types.js";
import { CLASSES } from "./data.js";
/* ---------- เลเวลและแต้ม ---------- */
/**
 * เพดาน Base Level = 135 ตามเป้าเกมเต็ม — โซนชุด A–E (PLAN ขั้น 5) ครบถึงยอดเขาตะเกียงสุดท้าย Lv 130–135
 * แล้ว (9 ต.ค. 2026) จึงไม่มีเลเวลตายที่ไม่มีที่ฟาร์ม · เพดานต้องเท่าเลเวลสูงสุดของโซนที่มีจริงเสมอ
 */
export const BASE_LEVEL_CAP = 135;
/**
 * Job Level สูงสุดตั้งต้นถ้าอาชีพไม่ได้กำหนด `jobCap` เอง (8 ต.ค. 2026: เดิม 20/40)
 * คลาสแรก 40 · คลาส 2 50 — เพดาน Job คือเงื่อนไขเลื่อนขั้นและงบแต้มสกิล (cap − 1 แต้ม)
 */
export const JOB_LEVEL_CAP_FIRST = 40;
export const JOB_LEVEL_CAP_ADVANCED = 50;
/** Job Level สูงสุดของอาชีพนี้ จุดเดียวที่ทุกฝั่งอ่าน ห้ามเทียบกับเลข 20/40 ตรง ๆ */
export function jobLevelCap(cls) {
    if (cls.jobCap !== undefined)
        return cls.jobCap;
    return cls.tier === 2 ? JOB_LEVEL_CAP_ADVANCED : JOB_LEVEL_CAP_FIRST;
}
export const STAT_CAP = 99;
/** EXP ที่ต้องใช้เพื่อขึ้น Base Level ถัดไป = round(40 × Lv^2.3) */
export function expToNextLevel(baseLevel) {
    return Math.round(40 * Math.pow(baseLevel, 2.3));
}
/**
 * EXP อาชีพที่ต้องใช้เพื่อขึ้น Job Level ถัดไป
 * GDD ไม่ได้ระบุสูตรนี้ไว้ ปรับ 8 ต.ค. 2026 ให้ Job 40 เต็มราว Base 60 และ Job 50 ราว Base 70
 * (Job EXP ต่อมอน = 40% ของ Base EXP): Job 40 ≈ 3.5M · Job 50 ≈ 7M EXP สะสม
 * ค่าสัมประสิทธิ์อยู่ที่นี่ที่เดียว ปรับตอน balance เฟส 4
 */
export const JOB_EXP_COEFF = 122;
export function jobExpToNextLevel(jobLevel) {
    return Math.round(JOB_EXP_COEFF * Math.pow(jobLevel, 2.1));
}
/**
 * สเตตัสที่ได้จาก Job Level ของอาชีพนี้ (เพิ่มทีละน้อยทุกเลเวล ไม่กินแต้มสเตตัส)
 *
 * คิดจาก `jobStats` (ต่อเลเวล) × Job Level แล้วปัดลง · อาชีพขั้นสูงได้โบนัสเต็มของอาชีพเดิมติดตัว
 * (เลื่อนขั้นได้เมื่อ Job เต็มเท่านั้น จึงเป็นค่าเต็ม) บวกโบนัสของ Job ใหม่ที่นับจาก 1
 * pure function ไม่มีสถานะ ฝั่ง server คิดจริง ฝั่ง client เรียกตัวเดียวกันเพื่อแสดง
 */
export function jobStatBonus(cls, jobLevel) {
    const out = {};
    const add = (def, level) => {
        if (!def?.jobStats)
            return;
        for (const k of STAT_KEYS) {
            const per = def.jobStats[k];
            if (per)
                out[k] = (out[k] ?? 0) + per * Math.max(0, level);
        }
    };
    if (cls.tier === 2 && cls.baseClass) {
        const base = CLASSES[cls.baseClass];
        if (base)
            add(base, jobLevelCap(base));
    }
    add(cls, Math.min(jobLevel, jobLevelCap(cls)));
    for (const k of STAT_KEYS) {
        if (out[k] !== undefined)
            out[k] = Math.floor(out[k] + 1e-9);
    }
    return out;
}
/** แต้มสเตตัสที่ได้เมื่อขึ้น Base Level = 3 + floor(Lv ÷ 5) */
export function statPointsForLevel(baseLevel) {
    return 3 + Math.floor(baseLevel / 5);
}
/** แต้มที่ต้องจ่ายเพื่อเพิ่มสเตตัสจาก x เป็น x+1 = floor((x − 1) ÷ 10) + 2 */
export function statCost(currentValue) {
    return Math.floor((currentValue - 1) / 10) + 2;
}
/* ---------- ค่ารอง ---------- */
/** HP สูงสุด = (100 + 20·Lv) × (1 + 0.01·VIT) ปรับด้วยตัวคูณอาชีพ */
export function maxHp(baseLevel, vit, hpMul = 1) {
    return Math.round((100 + 20 * baseLevel) * (1 + 0.01 * vit) * hpMul);
}
/** MP สูงสุด = (30 + 5·Lv) × (1 + 0.01·INT) */
export function maxMp(baseLevel, int) {
    return Math.round((30 + 5 * baseLevel) * (1 + 0.01 * int));
}
/**
 * ATK = 2·main + floor(sub/5) + floor(LUK/5) + อาวุธ
 * GDD เขียนสูตรโดยใช้ STR เป็น main และ DEX เป็น sub
 * อาชีพยิงไกลสลับให้ DEX เป็น main (ดู ClassDef.mainStat)
 */
export function attack(main, sub, luk, weaponAtk) {
    return 2 * main + Math.floor(sub / 5) + Math.floor(luk / 5) + weaponAtk;
}
/** MATK = 2·INT + floor(INT²/100) + อาวุธ */
export function magicAttack(int, weaponMatk) {
    return 2 * int + Math.floor((int * int) / 100) + weaponMatk;
}
/** HIT = Lv + DEX */
export function hit(baseLevel, dex) {
    return baseLevel + dex;
}
/** FLEE = Lv + AGI */
export function flee(baseLevel, agi) {
    return baseLevel + agi;
}
/** โอกาสคริ (%) = 1 + 0.3·LUK */
export function critChance(luk) {
    return 1 + 0.3 * luk;
}
/** โอกาสโจมตีโดน (%) = 80 + HIT − FLEE จำกัดช่วง 5–95 */
export function hitChance(attackerHit, targetFlee) {
    return clamp(80 + attackerHit - targetFlee, 5, 95);
}
/** VIT/2 ของเป้าหมายลดดาเมจได้ไม่เกินสัดส่วนนี้ของดาเมจครั้งนั้น */
export const VIT_REDUCE_CAP = 0.3;
/**
 * ดาเมจ = ATK × Skill% × 100/(100 + DEF) − VIT เป้าหมาย/2 (หักได้ไม่เกิน 30% ของดาเมจ)
 * คริคูณ 1.5 และไม่สนเกราะ (ข้าม DEF)
 *
 * @param power       ATK หรือ MATK ของผู้โจมตี
 * @param skillMul    ตัวคูณสกิล เช่น 1.8 สำหรับสกิล 180%
 * @param targetDef   DEF ของเป้าหมาย
 * @param targetVit   VIT ของเป้าหมาย ใช้ลดดาเมจแบบคงที่
 * @param isCrit      คริหรือไม่
 * @returns ดาเมจจำนวนเต็ม ต่ำสุด 1
 */
export function damage(power, skillMul, targetDef, targetVit, isCrit = false) {
    const armorFactor = isCrit ? 1 : 100 / (100 + targetDef);
    const hit = power * skillMul * armorFactor * (isCrit ? 1.5 : 1);
    // VIT/2 หักได้ไม่เกิน VIT_REDUCE_CAP ของดาเมจ (ผู้ใช้อนุมัติ 9 ต.ค. 2026) — เดิมแทงก์ VIT สูงหักจนมอนตีไม่เข้า
    const raw = hit - Math.min(targetVit / 2, hit * VIT_REDUCE_CAP);
    return Math.max(1, Math.round(raw));
}
/** น้ำหนักกระเป๋าที่แบกได้ = 2000 + 30·STR */
export function maxCarryWeight(str) {
    return 2000 + 30 * str;
}
/* ---------- ช่องกระเป๋า ---------- */
/** ช่องกระเป๋าทั้งหมดที่วาดบนหน้าจอ ส่วนที่ยังไม่ปลดล็อกโชว์เป็นช่องล็อก */
export const BAG_SLOTS_MAX = 40;
/** ช่องที่ได้ตั้งแต่เลเวล 1 */
export const BAG_SLOTS_BASE = 12;
/** ได้เพิ่มกี่ช่องต่อหนึ่งช่วงเลเวล */
export const BAG_SLOTS_PER_STEP = 3;
/** ช่วงเลเวลที่ปลดล็อกช่องเพิ่ม */
export const BAG_SLOTS_STEP = 5;
/**
 * ช่องกระเป๋าที่ใช้ได้ที่ Base Level นี้ = 12 + 3·floor(Lv ÷ 5) ตันที่ 40
 *
 * **ยังเป็นกติกาของการแสดงผลเท่านั้น** server ไม่ได้ปฏิเสธของที่เกินช่อง
 * ตัวจำกัดจริงตาม GDD คือน้ำหนัก (`maxCarryWeight`) ซึ่งก็ยังไม่ได้บังคับเหมือนกัน
 * วางสูตรไว้ใน shared ตั้งแต่ตอนนี้เพื่อให้วันที่ server บังคับจริง ทั้งสองฝั่งนับตรงกัน
 */
export function bagSlots(baseLevel) {
    const earned = BAG_SLOTS_BASE + BAG_SLOTS_PER_STEP * Math.floor(baseLevel / BAG_SLOTS_STEP);
    return clamp(earned, BAG_SLOTS_BASE, BAG_SLOTS_MAX);
}
/** Base Level ที่จะปลดล็อกช่องถัดไป คืน 0 เมื่อปลดครบแล้ว */
export function nextBagSlotLevel(baseLevel) {
    if (bagSlots(baseLevel) >= BAG_SLOTS_MAX)
        return 0;
    return (Math.floor(baseLevel / BAG_SLOTS_STEP) + 1) * BAG_SLOTS_STEP;
}
/* ---------- ค่ารองรวมชุดเดียว ---------- */
/** โอกาสบล็อกสูงสุด (บัฟ + โล่ + สกิลติดตัวซ้อนกันแล้วต้องยังโดนตีบ้าง) */
export const BLOCK_CAP = 0.75;
/**
 * เพดานคริ (%) และความเร็วตี (ครั้ง/วิ) — audit รอบ 18
 * คริไม่สนเกราะ ถ้าไม่มีเพดาน แอสแซสซินปลายเกม (LUK + ของ + การ์ด) คริเกือบ 100% แล้ว DEF บอสไม่มีความหมาย
 * ความเร็วตีเกิน ~5 ครั้ง/วิ ชนรอบ game loop (10 Hz) และแซงสายอื่นแบบทวีคูณ
 */
export const CRIT_CAP = 75;
export const ASPD_CAP = 5;
/**
 * คำนวณค่ารองทั้งชุดจากสเตตัส อาชีพ และเลเวล
 * แทนที่ฟังก์ชัน D() ในเดโม แต่ไม่อ่าน global state
 */
export function deriveStats(cls, stats, baseLevel, mods = {}) {
    const main = stats[cls.mainStat];
    const sub = cls.mainStat === "STR" ? stats.DEX : stats.STR;
    const wAtk = mods.weaponAtk ?? cls.weaponAtk;
    const wMatk = mods.weaponMatk ?? cls.weaponMatk;
    return {
        maxHp: maxHp(baseLevel, stats.VIT, cls.hpMul * (mods.hpMul ?? 1)),
        maxMp: Math.round(maxMp(baseLevel, stats.INT) * (mods.mpMul ?? 1)),
        atk: Math.round(attack(main, sub, stats.LUK, wAtk) * (mods.atkMul ?? 1)),
        matk: Math.round(magicAttack(stats.INT, wMatk) * (mods.matkMul ?? 1)),
        def: cls.armor + (mods.flatDef ?? 0),
        hit: hit(baseLevel, stats.DEX) + (mods.flatHit ?? 0),
        flee: flee(baseLevel, stats.AGI) + (mods.flatFlee ?? 0),
        crit: Math.min(CRIT_CAP, critChance(stats.LUK) + (mods.flatCrit ?? 0)),
        aspd: Math.min(ASPD_CAP, cls.aspd * (1 + 0.01 * stats.AGI + 0.002 * stats.DEX) * (mods.aspdMul ?? 1)),
        block: Math.min(BLOCK_CAP, cls.block + (mods.flatBlock ?? 0)),
        softDef: stats.VIT / 2,
    };
}
/* ---------- ตัวช่วย ---------- */
export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
//# sourceMappingURL=formulas.js.map